"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import { countRemainingOnboardingSteps } from "../lib/provider/onboardingStatus"
import get from "lodash/get"
import useAccountResource from "./useAccountResource"
import useSessionAttributes from "../lib/auth/useSessionAttributes"

export type ProviderData = {
  provider?: {
    id?: number | null
    isFullyOnboarded?: boolean
    profileContent?: Record<string, unknown> | null
    published?: boolean
    onboardingStatusV2?: { nodes?: Array<Record<string, unknown>> }
    onboardingStatusV4?: { nodes?: Array<Record<string, unknown>> }
    totalPendingCohortUsers?: string | number
  }
  sessions?: { totalCount?: string | number }
}

export type UseGetProviderOptions = {
  enabled?: boolean
  /** When set, fetches this provider via `/edge/get-my-provider?providerId=` (e.g. admin cohort view). */
  providerId?: number | null
}

export type UseGetProviderState = {
  loading: boolean
  error: unknown
  provider?: ProviderData["provider"] | null
  isFullyOnboarded: boolean
  onboardingStatusV2: Record<string, unknown> | null
  onboardingStatus: Record<string, unknown> | null
  backgroundComplete: boolean
  backgroundSubmitted: boolean
  certificationComplete: boolean
  certificationSubmitted: boolean
  cohortComplete: boolean
  documentsOverallComplete: boolean
  groupComplete: boolean
  groupPendingPublication: boolean
  insuranceComplete: boolean
  insuranceSubmitted: boolean
  latestGroupId: string | number | boolean
  paymentConnectedAccountComplete: boolean
  paymentExternalAccountComplete: boolean
  paymentTransactOffPlatform: boolean
  profileComplete: boolean
  profilePendingPublication: boolean
  serviceAgreementComplete: boolean
  baaHipaatizerComplete: boolean
  baaZestComplete: boolean
  profileContent?: Record<string, unknown> | null
  totalPendingCohortUsers: number
  sessionsTotalCount: number
  isProviderPublished: boolean
  countRemainingOnboardingSteps: number
}

export default function useGetProvider(
  options?: UseGetProviderOptions,
): readonly [UseGetProviderState, { refetch: () => Promise<void> }] {
  const sessionAttrs = useSessionAttributes()
  const providerId =
    options?.providerId !== undefined ? options.providerId : sessionAttrs.providerId
  const enabled =
    options?.enabled !== false && sessionAttrs.isAuthenticated && !!providerId
  const {
    data: providerData,
    loading,
    error,
    refetch: fetchData,
  } = useAccountResource<ProviderData>(
    enabled
      ? `/edge/get-my-provider?providerId=${encodeURIComponent(String(providerId))}`
      : null,
    sessionAttrs.resourceScope,
  )

  const provider = providerData?.provider
  const onboardingStatusV2 = get(provider, "onboardingStatusV2.nodes[0]", null)
  const onboardingStatus = get(provider, "onboardingStatusV4.nodes[0]", null)
  const certificationComplete = get(onboardingStatus, "certificationComplete", false)
  const certificationSubmitted = get(
    onboardingStatus,
    "certificationSubmitted",
    false,
  )
  const cohortComplete = get(onboardingStatus, "cohortComplete", false)
  const documentsOverallComplete = get(
    onboardingStatus,
    "documentsOverallComplete",
    false,
  )
  const groupComplete = get(onboardingStatus, "groupComplete", false)
  const groupPendingPublication = get(
    onboardingStatus,
    "groupPendingPublication",
    false,
  )
  const latestGroupId = get(onboardingStatus, "latestGroupId", false)
  const profileComplete = get(onboardingStatus, "profileComplete", false)
  const profilePendingPublication = get(
    onboardingStatus,
    "profilePendingPublication",
    false,
  )
  const baaHipaatizerComplete = get(onboardingStatus, "baaHipaatizerComplete", false)
  const baaZestComplete = get(onboardingStatus, "baaZestComplete", false)
  const totalPendingCohortUsers = Number.parseInt(
    String(get(provider, "totalPendingCohortUsers", "0")),
    10,
  )
  const sessionsTotalCount = Number.parseInt(
    String(get(providerData, "sessions.totalCount", "0")),
    10,
  )
  const isFullyOnboarded = Boolean(provider?.isFullyOnboarded)
  const profileContent = provider?.profileContent
  const isProviderPublished = Boolean(provider?.published)

  return [
    {
      loading,
      error,
      provider,
      isFullyOnboarded,
      onboardingStatusV2,
      onboardingStatus,
      backgroundComplete: get(onboardingStatusV2, "backgroundComplete", false),
      backgroundSubmitted: get(onboardingStatusV2, "backgroundSubmitted", false),
      certificationComplete,
      certificationSubmitted,
      cohortComplete,
      documentsOverallComplete,
      groupComplete,
      groupPendingPublication,
      insuranceComplete: get(onboardingStatusV2, "insuranceComplete", false),
      insuranceSubmitted: get(onboardingStatusV2, "insuranceSubmitted", false),
      latestGroupId,
      paymentConnectedAccountComplete: get(
        onboardingStatusV2,
        "paymentConnectedAccountComplete",
        false,
      ),
      paymentExternalAccountComplete: get(
        onboardingStatusV2,
        "paymentExternalAccountComplete",
        false,
      ),
      paymentTransactOffPlatform: get(
        onboardingStatusV2,
        "paymentTransactOffPlatform",
        false,
      ),
      profileComplete,
      profilePendingPublication,
      serviceAgreementComplete: get(
        onboardingStatusV2,
        "serviceAgreementComplete",
        false,
      ),
      baaHipaatizerComplete,
      baaZestComplete,
      profileContent,
      totalPendingCohortUsers,
      sessionsTotalCount,
      isProviderPublished,
      countRemainingOnboardingSteps: countRemainingOnboardingSteps(onboardingStatus),
    },
    { refetch: fetchData },
  ] as const
}
